import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Role } from '@hirenfire/shared';
import api from '@/lib/api';
import { useAuthStore } from '@/stores/authStore';
import { resetSocket } from '@/lib/socket';

interface AuthResponse {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
  user: {
    id: string;
    email: string;
    role: string;
  };
}

export function useAuth() {
  const navigate = useNavigate();
  const { user, isAuthenticated, isLoading, login: storeLogin, logout: storeLogout, setLoading } = useAuthStore();

  const login = useCallback(
    async (data: { email: string; password: string }) => {
      const res = await api.post<{ success: boolean; data: AuthResponse }>(
        '/auth/login',
        data,
      );
      const payload = res.data.data;
      storeLogin(payload.user as any, payload.accessToken, payload.refreshToken);
      return payload.user;
    },
    [storeLogin],
  );

  const register = useCallback(
    async (data: { email: string; password: string; role: string }) => {
      const res = await api.post<{ success: boolean; data: AuthResponse }>(
        '/auth/register',
        data,
      );
      const payload = res.data.data;
      storeLogin(payload.user as any, payload.accessToken, payload.refreshToken);
      return payload.user;
    },
    [storeLogin],
  );

  const logout = useCallback(async () => {
    try {
      await api.post('/auth/logout');
    } catch {
      // ignore
    }
    resetSocket();
    storeLogout();
    navigate('/login');
  }, [storeLogout, navigate]);

  const fetchMe = useCallback(async () => {
    const token = useAuthStore.getState().accessToken;
    if (!token) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const res = await api.get<{ success: boolean; data: any }>('/profile/me');
      storeLogin(res.data.data, token);
    } catch {
      storeLogout();
    } finally {
      setLoading(false);
    }
  }, [storeLogin, storeLogout, setLoading]);

  const getDashboardPath = useCallback(() => {
    if (!user) return '/login';
    switch (user.role) {
      case Role.ADMIN:
        return '/admin/users';
      case Role.AGENCY:
      case Role.WORKER:
      default:
        return '/jobs';
    }
  }, [user]);

  return {
    user,
    isAuthenticated,
    isLoading,
    login,
    register,
    logout,
    fetchMe,
    getDashboardPath,
  };
}
